import { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, ApiError } from '@/modules/api/api-client';
import { queryKeys } from '@/modules/query/query-keys';
import { ProjectRoleEntry, useAuthStore, UserProfile } from '@/stores/auth-store';
import { useToastStore } from '@/stores/toast-store';
import { useUIStore } from '@/stores/ui-store';

// --- Types ---

interface ChannelConfig {
  type: 'in_app' | 'email';
  enabled: boolean;
}

interface EventRoutingRule {
  eventType: string;
  channels: string[];
}

interface DigestConfig {
  enabled: boolean;
  frequency: 'daily' | 'weekly';
  time: string; // HH:mm
}

interface MuteRule {
  id: string;
  repositoryName?: string;
  eventType?: string;
  until?: string; // ISO 8601
}

interface NotificationPreferences {
  channels: ChannelConfig[];
  eventRouting: EventRoutingRule[];
  digest: DigestConfig;
  muteRules: MuteRule[];
}

// --- Loading Skeleton ---

function SettingsSkeleton() {
  return (
    <div className="space-y-6 animate-pulse" aria-label="Loading settings">
      {/* Profile skeleton */}
      <div className="bg-surface rounded-lg p-6 shadow-sm border border-border">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-4" />
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-64" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-56" />
        </div>
      </div>
      {/* Appearance skeleton */}
      <div className="bg-surface rounded-lg p-6 shadow-sm border border-border">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-4" />
        <div className="space-y-3">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
        </div>
      </div>
      {/* Notification preferences skeleton */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-56 mb-4" />
        <div className="space-y-3">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full" />
        </div>
      </div>
    </div>
  );
}

// --- Profile Section ---

function ProjectRoleItem({ entry }: { entry: ProjectRoleEntry }) {
  return (
    <li className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm text-gray-900 dark:text-gray-100">
      <span className="font-medium">{entry.project}</span>
      <span className="text-gray-500 dark:text-gray-400">—</span>
      <span>{entry.role}</span>
      {entry.environments && entry.environments.length > 0 && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          (environments: {entry.environments.join(', ')})
        </span>
      )}
      {(!entry.environments || entry.environments.length === 0) && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          (all environments)
        </span>
      )}
    </li>
  );
}

function ProfileSection({ user }: { user: UserProfile }) {
  return (
    <section
      className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
      aria-labelledby="profile-heading"
    >
      <h2 id="profile-heading" className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Profile
      </h2>
      <dl className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:gap-4">
          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 sm:w-32">Name</dt>
          <dd className="text-sm text-gray-900 dark:text-gray-100">{user.displayName}</dd>
        </div>
        <div className="flex flex-col sm:flex-row sm:gap-4">
          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 sm:w-32">Email</dt>
          <dd className="text-sm text-gray-900 dark:text-gray-100">{user.email}</dd>
        </div>
        <div className="flex flex-col sm:flex-row sm:gap-4">
          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 sm:w-32">Role</dt>
          <dd className="text-sm text-gray-900 dark:text-gray-100">{user.role}</dd>
        </div>
        {user.globalRole && (
          <div className="flex flex-col sm:flex-row sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 sm:w-32">Global Role</dt>
            <dd className="text-sm text-gray-900 dark:text-gray-100">{user.globalRole}</dd>
          </div>
        )}
        {user.projectRoles && user.projectRoles.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 sm:w-32">Project Roles</dt>
            <dd className="text-sm text-gray-900 dark:text-gray-100">
              <ul className="space-y-1" role="list" aria-label="Project role assignments">
                {user.projectRoles.map((entry) => (
                  <ProjectRoleItem key={`${entry.project}-${entry.role}-${entry.environments?.join(',') ?? 'all'}`} entry={entry} />
                ))}
              </ul>
            </dd>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:gap-4">
          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 sm:w-32">Repositories</dt>
          <dd className="text-sm text-gray-900 dark:text-gray-100">
            {user.repositories.length > 0
              ? user.repositories.join(', ')
              : 'No repositories assigned'}
          </dd>
        </div>
      </dl>
    </section>
  );
}

// --- Appearance Section ---

function AppearanceSection() {
  const { theme, diffViewMode, setTheme, setDiffViewMode } = useUIStore();

  const selectedClass = 'bg-brand-100 text-brand-800 border-brand-300 dark:bg-brand-900/50 dark:text-brand-200 dark:border-brand-600';
  const unselectedClass = 'bg-surface text-content-secondary border-border hover:bg-surface-raised dark:hover:bg-surface-overlay';

  return (
    <section
      className="bg-surface rounded-lg p-6 shadow-sm border border-border"
      aria-labelledby="appearance-heading"
    >
      <h2 id="appearance-heading" className="text-lg font-semibold text-content mb-4">
        Appearance
      </h2>
      <div className="space-y-4">
        {/* Theme toggle */}
        <fieldset>
          <legend className="text-sm font-medium text-content-secondary mb-2">
            Theme
          </legend>
          <div className="flex gap-3" role="radiogroup" aria-label="Theme preference">
            <label
              className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium cursor-pointer min-h-[44px] border focus-within:ring-2 focus-within:ring-brand-600 focus-within:ring-offset-2 ${
                theme === 'light' ? selectedClass : unselectedClass
              }`}
            >
              <input
                type="radio"
                name="theme"
                value="light"
                checked={theme === 'light'}
                onChange={() => setTheme('light')}
                className="sr-only"
              />
              Light
            </label>
            <label
              className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium cursor-pointer min-h-[44px] border focus-within:ring-2 focus-within:ring-brand-600 focus-within:ring-offset-2 ${
                theme === 'dark' ? selectedClass : unselectedClass
              }`}
            >
              <input
                type="radio"
                name="theme"
                value="dark"
                checked={theme === 'dark'}
                onChange={() => setTheme('dark')}
                className="sr-only"
              />
              Dark
            </label>
            <label
              className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium cursor-pointer min-h-[44px] border focus-within:ring-2 focus-within:ring-brand-600 focus-within:ring-offset-2 ${
                theme === 'system' ? selectedClass : unselectedClass
              }`}
            >
              <input
                type="radio"
                name="theme"
                value="system"
                checked={theme === 'system'}
                onChange={() => setTheme('system')}
                className="sr-only"
              />
              System
            </label>
          </div>
        </fieldset>

        {/* Diff view mode toggle */}
        <fieldset>
          <legend className="text-sm font-medium text-content-secondary mb-2">
            Diff View Mode
          </legend>
          <div className="flex gap-3" role="radiogroup" aria-label="Diff view mode preference">
            <label
              className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium cursor-pointer min-h-[44px] border focus-within:ring-2 focus-within:ring-brand-600 focus-within:ring-offset-2 ${
                diffViewMode === 'unified' ? selectedClass : unselectedClass
              }`}
            >
              <input
                type="radio"
                name="diffViewMode"
                value="unified"
                checked={diffViewMode === 'unified'}
                onChange={() => setDiffViewMode('unified')}
                className="sr-only"
              />
              Unified
            </label>
            <label
              className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium cursor-pointer min-h-[44px] border focus-within:ring-2 focus-within:ring-brand-600 focus-within:ring-offset-2 ${
                diffViewMode === 'split' ? selectedClass : unselectedClass
              }`}
            >
              <input
                type="radio"
                name="diffViewMode"
                value="split"
                checked={diffViewMode === 'split'}
                onChange={() => setDiffViewMode('split')}
                className="sr-only"
              />
              Split
            </label>
          </div>
        </fieldset>
      </div>
    </section>
  );
}

// --- Notification Preferences Section ---

interface NotificationPreferencesSectionProps {
  preferences: NotificationPreferences;
  onSave: (prefs: NotificationPreferences) => void;
  isSaving: boolean;
  validationErrors: Record<string, string>;
}

function NotificationPreferencesSection({
  preferences,
  onSave,
  isSaving,
  validationErrors,
}: NotificationPreferencesSectionProps) {
  const [channels, setChannels] = useState<ChannelConfig[]>(preferences.channels);
  const [eventRouting, setEventRouting] = useState<EventRoutingRule[]>(preferences.eventRouting);
  const [digest, setDigest] = useState<DigestConfig>(preferences.digest);
  const [muteRules, setMuteRules] = useState<MuteRule[]>(preferences.muteRules);

  // Sync state when preferences prop changes (e.g., after refetch)
  useEffect(() => {
    setChannels(preferences.channels);
    setEventRouting(preferences.eventRouting);
    setDigest(preferences.digest);
    setMuteRules(preferences.muteRules);
  }, [preferences]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSave({ channels, eventRouting, digest, muteRules });
    },
    [channels, eventRouting, digest, muteRules, onSave],
  );

  const handleChannelToggle = useCallback((index: number) => {
    setChannels((prev) =>
      prev.map((ch, i) => (i === index ? { ...ch, enabled: !ch.enabled } : ch)),
    );
  }, []);

  const handleEventRoutingChannelToggle = useCallback(
    (ruleIndex: number, channel: string) => {
      setEventRouting((prev) =>
        prev.map((rule, i) => {
          if (i !== ruleIndex) return rule;
          const hasChannel = rule.channels.includes(channel);
          return {
            ...rule,
            channels: hasChannel
              ? rule.channels.filter((c) => c !== channel)
              : [...rule.channels, channel],
          };
        }),
      );
    },
    [],
  );

  const handleDigestEnabledToggle = useCallback(() => {
    setDigest((prev) => ({ ...prev, enabled: !prev.enabled }));
  }, []);

  const handleDigestFrequencyChange = useCallback((frequency: 'daily' | 'weekly') => {
    setDigest((prev) => ({ ...prev, frequency }));
  }, []);

  const handleDigestTimeChange = useCallback((time: string) => {
    setDigest((prev) => ({ ...prev, time }));
  }, []);

  const handleRemoveMuteRule = useCallback((id: string) => {
    setMuteRules((prev) => prev.filter((rule) => rule.id !== id));
  }, []);

  return (
    <section
      className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
      aria-labelledby="notifications-heading"
    >
      <h2 id="notifications-heading" className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Notification Preferences
      </h2>

      {validationErrors.form && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md" role="alert">
          <p className="text-sm text-red-700 dark:text-red-300">{validationErrors.form}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Channel Configuration */}
        <fieldset>
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Notification Channels
          </legend>
          <div className="space-y-2">
            {channels.map((channel, index) => (
              <label
                key={channel.type}
                className="flex items-center gap-3 cursor-pointer min-h-[44px]"
              >
                <input
                  type="checkbox"
                  checked={channel.enabled}
                  onChange={() => handleChannelToggle(index)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
                />
                <span className="text-sm text-gray-900 dark:text-gray-100 capitalize">
                  {channel.type === 'in_app' ? 'In-App' : 'Email'}
                </span>
              </label>
            ))}
          </div>
          {validationErrors.channels && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {validationErrors.channels}
            </p>
          )}
        </fieldset>

        {/* Event Routing */}
        <fieldset>
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Event Routing
          </legend>
          <div className="space-y-3">
            {eventRouting.map((rule, ruleIndex) => (
              <div key={rule.eventType} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <span className="text-sm text-gray-900 dark:text-gray-100 sm:w-40 capitalize">
                  {rule.eventType.replace(/_/g, ' ')}
                </span>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1.5 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={rule.channels.includes('in_app')}
                      onChange={() => handleEventRoutingChannelToggle(ruleIndex, 'in_app')}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400">In-App</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={rule.channels.includes('email')}
                      onChange={() => handleEventRoutingChannelToggle(ruleIndex, 'email')}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Email</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
          {validationErrors.eventRouting && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {validationErrors.eventRouting}
            </p>
          )}
        </fieldset>

        {/* Digest Settings */}
        <fieldset>
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Digest Settings
          </legend>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer min-h-[44px]">
              <input
                type="checkbox"
                checked={digest.enabled}
                onChange={handleDigestEnabledToggle}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
              />
              <span className="text-sm text-gray-900 dark:text-gray-100">
                Enable digest emails
              </span>
            </label>

            {digest.enabled && (
              <div className="ml-7 space-y-3">
                <div className="flex items-center gap-3">
                  <label htmlFor="digest-frequency" className="text-sm text-gray-700 dark:text-gray-300">
                    Frequency
                  </label>
                  <select
                    id="digest-frequency"
                    value={digest.frequency}
                    onChange={(e) => handleDigestFrequencyChange(e.target.value as 'daily' | 'weekly')}
                    className="rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 min-h-[44px]"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <label htmlFor="digest-time" className="text-sm text-gray-700 dark:text-gray-300">
                    Time
                  </label>
                  <input
                    id="digest-time"
                    type="time"
                    value={digest.time}
                    onChange={(e) => handleDigestTimeChange(e.target.value)}
                    className="rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 min-h-[44px]"
                  />
                </div>
              </div>
            )}
          </div>
          {validationErrors.digest && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {validationErrors.digest}
            </p>
          )}
        </fieldset>

        {/* Mute Rules */}
        <fieldset>
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Mute Rules
          </legend>
          {muteRules.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No mute rules configured.
            </p>
          ) : (
            <ul className="space-y-2" role="list" aria-label="Mute rules">
              {muteRules.map((rule) => (
                <li
                  key={rule.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md"
                >
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    {rule.repositoryName && (
                      <span>Repo: {rule.repositoryName}</span>
                    )}
                    {rule.repositoryName && rule.eventType && <span> · </span>}
                    {rule.eventType && (
                      <span>Event: {rule.eventType.replace(/_/g, ' ')}</span>
                    )}
                    {rule.until && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                        (until {new Date(rule.until).toLocaleDateString()})
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveMuteRule(rule.id)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium min-h-[44px] min-w-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded"
                    aria-label={`Remove mute rule${rule.repositoryName ? ` for ${rule.repositoryName}` : ''}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          {validationErrors.muteRules && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {validationErrors.muteRules}
            </p>
          )}
        </fieldset>

        {/* Save button */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 min-h-[44px]"
          >
            {isSaving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </form>
    </section>
  );
}

// --- Main Settings Page ---

export function SettingsPage() {
  const user = useAuthStore((state) => state.user);
  const addToast = useToastStore((state) => state.addToast);
  const queryClient = useQueryClient();
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Fetch user profile
  const {
    data: profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
  } = useQuery<UserProfile>({
    queryKey: queryKeys.user.profile,
    queryFn: () => apiClient.get<UserProfile>('/auth/me'),
    staleTime: 300_000, // 5 minutes
    // Use the auth store user as initial data if available
    initialData: user ?? undefined,
  });

  // Fetch notification preferences
  const {
    data: preferences,
    isLoading: isPreferencesLoading,
    isError: isPreferencesError,
  } = useQuery<NotificationPreferences>({
    queryKey: queryKeys.user.preferences,
    queryFn: () => apiClient.get<NotificationPreferences>('/notifications/preferences'),
    staleTime: 300_000,
  });

  // Save notification preferences mutation
  const saveMutation = useMutation({
    mutationFn: (updatedPrefs: NotificationPreferences) =>
      apiClient.put<NotificationPreferences>('/notifications/preferences', updatedPrefs),
    onSuccess: () => {
      setValidationErrors({});
      addToast({ type: 'success', message: 'Preferences saved successfully.', duration: 5000 });
      queryClient.invalidateQueries({ queryKey: queryKeys.user.preferences });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.status === 400) {
        const body = error.body as { errors?: Record<string, string>; message?: string } | undefined;
        if (body?.errors) {
          setValidationErrors(body.errors);
        } else if (body?.message) {
          setValidationErrors({ form: body.message });
        } else {
          setValidationErrors({ form: 'Validation failed. Please check your inputs.' });
        }
      } else {
        addToast({ type: 'error', message: 'Failed to save preferences. Please try again.', duration: 8000 });
      }
    },
  });

  const handleSave = useCallback(
    (updatedPrefs: NotificationPreferences) => {
      setValidationErrors({});
      saveMutation.mutate(updatedPrefs);
    },
    [saveMutation],
  );

  const handleRetry = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.user.profile });
    queryClient.invalidateQueries({ queryKey: queryKeys.user.preferences });
  }, [queryClient]);

  return (
    <main id="main-content" className="max-w-3xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        Settings
      </h1>

      <div className="space-y-6">
        {/* Read-only profile section */}
        {isProfileLoading && <SettingsSkeleton />}
        {isProfileError && !isProfileLoading && (
          <div className="text-center py-8">
            <p className="text-red-600 dark:text-red-400 mb-4">
              Profile could not be loaded. Please try again.
            </p>
            <button
              type="button"
              onClick={handleRetry}
              className="px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 min-h-[44px]"
            >
              Retry
            </button>
          </div>
        )}
        {!isProfileLoading && !isProfileError && profile && (
          <ProfileSection user={profile} />
        )}

        {/* Appearance section (applies immediately, no save needed) */}
        <AppearanceSection />

        {/* Notification preferences (editable, requires save) */}
        {isPreferencesLoading && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm animate-pulse" aria-label="Loading notification preferences">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-56 mb-4" />
            <div className="space-y-3">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            </div>
          </div>
        )}
        {isPreferencesError && !isPreferencesLoading && (
          <section className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Notification Preferences
            </h2>
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Notification preferences are currently unavailable.
              </p>
              <button
                type="button"
                onClick={handleRetry}
                className="mt-3 px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 min-h-[44px]"
              >
                Retry
              </button>
            </div>
          </section>
        )}
        {!isPreferencesLoading && !isPreferencesError && preferences && (
          <NotificationPreferencesSection
            preferences={preferences}
            onSave={handleSave}
            isSaving={saveMutation.isPending}
            validationErrors={validationErrors}
          />
        )}
      </div>
    </main>
  );
}
